import { useState, useImperativeHandle, forwardRef } from 'react'
import { Button, Modal, Form, Input, Space } from 'antd'
import { LockOutlined, UnlockOutlined, MessageOutlined, DeleteOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import * as devicesApi from '../../api/devices.api'
import { toast, handleMutationError } from '../../lib/notification'
import { Can, P } from '../../rbac'

interface Props {
  deviceId: string
  isOnline: boolean
  /** When true, the action buttons are hidden and only modals are rendered. Use the ref to open modals. */
  headless?: boolean
}

export interface DeviceActionsRef {
  openLock: () => void
  openUnlock: () => void
  openMessage: () => void
  openDestroy: () => void
}

const DeviceActions = forwardRef<DeviceActionsRef, Props>(function DeviceActions(
  { deviceId, isOnline, headless = false },
  ref
) {
  const [lockModalOpen, setLockModalOpen] = useState(false)
  const [unlockModalOpen, setUnlockModalOpen] = useState(false)
  const [messageModalOpen, setMessageModalOpen] = useState(false)
  const [destroyModalOpen, setDestroyModalOpen] = useState(false)
  const [lockForm] = Form.useForm()
  const [unlockForm] = Form.useForm()
  const [messageForm] = Form.useForm()
  const queryClient = useQueryClient()

  useImperativeHandle(ref, () => ({
    openLock: () => setLockModalOpen(true),
    openUnlock: () => setUnlockModalOpen(true),
    openMessage: () => setMessageModalOpen(true),
    openDestroy: () => setDestroyModalOpen(true),
  }))

  const lockMutation = useMutation({
    mutationFn: (values: { reason: string; unlockPassword: string }) =>
      devicesApi.lockDevice(deviceId, values),
    onSuccess: (data) => {
      toast.success(data.message)
      setLockModalOpen(false)
      lockForm.resetFields()
      queryClient.invalidateQueries({ queryKey: ['devices', deviceId] })
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to lock device')
    },
  })

  const unlockMutation = useMutation({
    mutationFn: (values: { message?: string }) =>
      devicesApi.unlockDevice(deviceId, values),
    onSuccess: (data) => {
      toast.success(data.message)
      setUnlockModalOpen(false)
      unlockForm.resetFields()
      queryClient.invalidateQueries({ queryKey: ['devices', deviceId] })
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to unlock device')
    },
  })

  const messageMutation = useMutation({
    mutationFn: (values: { message: string }) =>
      devicesApi.sendMessage(deviceId, values),
    onSuccess: (data) => {
      toast.success(data.message)
      setMessageModalOpen(false)
      messageForm.resetFields()
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to send message')
    },
  })

  const destroyMutation = useMutation({
    mutationFn: () => devicesApi.wipeDevice(deviceId),
    onSuccess: () => {
      toast.success('Device destroyed successfully')
      setDestroyModalOpen(false)
      queryClient.invalidateQueries({ queryKey: ['devices', deviceId] })
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to destroy device')
    },
  })

  const handleLockSubmit = () => {
    lockForm.validateFields().then((values) => {
      lockMutation.mutate(values)
    })
  }

  const handleUnlockSubmit = () => {
    unlockForm.validateFields().then((values) => {
      unlockMutation.mutate(values)
    })
  }

  const handleMessageSubmit = () => {
    messageForm.validateFields().then((values) => {
      messageMutation.mutate(values)
    })
  }

  return (
    <>
      {!headless && (
        <Space size="middle">
          <Can permission={P.device_operations.device.lock}>
            <Button
              type="primary"
              danger
              icon={<LockOutlined />}
              onClick={() => setLockModalOpen(true)}
              disabled={!isOnline}
              style={{
                height: 40,
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Lock Device
            </Button>
          </Can>

          <Can permission={P.device_operations.device.unlock}>
            <Button
              type="primary"
              icon={<UnlockOutlined />}
              onClick={() => setUnlockModalOpen(true)}
              disabled={!isOnline}
              style={{
                height: 40,
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 500,
                background: '#10b981',
                borderColor: '#10b981',
              }}
            >
              Unlock Device
            </Button>
          </Can>

          <Can permission={P.device_operations.device.notify}>
            <Button
              icon={<MessageOutlined />}
              onClick={() => setMessageModalOpen(true)}
              disabled={!isOnline}
              style={{
                height: 40,
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Send Message
            </Button>
          </Can>
        </Space>
      )}

      {/* Lock Device Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LockOutlined style={{ color: '#ef4444' }} />
            <span>Lock Device</span>
          </div>
        }
        open={lockModalOpen}
        onOk={handleLockSubmit}
        onCancel={() => {
          setLockModalOpen(false)
          lockForm.resetFields()
        }}
        okText="Lock Device"
        okButtonProps={{
          danger: true,
          loading: lockMutation.isPending,
        }}
        width={600}
      >
        <div style={{ marginTop: 24 }}>
          <p style={{ marginBottom: 16, color: '#64748b' }}>
            This will lock the device and display a full-screen message. The device will require
            a password or remote unlock to access.
          </p>
          <Form form={lockForm} layout="vertical">
            <Form.Item
              name="reason"
              label="Lock Reason"
              rules={[{ required: true, message: 'Please enter a reason for locking' }, { max: 1000, message: 'Reason must not exceed 1000 characters' }]}
            >
              <Input.TextArea
                rows={4}
                placeholder="e.g., Payment overdue. Please contact support at +91-XXXXXXXXXX"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
            <Form.Item
              name="unlockPassword"
              label="Unlock Password"
              rules={[
                { required: true, message: 'Please enter an unlock password' },
                { min: 6, message: 'Password must be at least 6 characters' },
              ]}
              extra="The user can unlock locally using this password, or you can unlock remotely."
            >
              <Input.Password
                placeholder="Enter a secure password"
                style={{ borderRadius: 8, height: 40 }}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      {/* Unlock Device Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UnlockOutlined style={{ color: '#10b981' }} />
            <span>Unlock Device</span>
          </div>
        }
        open={unlockModalOpen}
        onOk={handleUnlockSubmit}
        onCancel={() => {
          setUnlockModalOpen(false)
          unlockForm.resetFields()
        }}
        okText="Unlock Device"
        okButtonProps={{
          loading: unlockMutation.isPending,
          style: { background: '#10b981', borderColor: '#10b981' },
        }}
        width={600}
      >
        <div style={{ marginTop: 24 }}>
          <p style={{ marginBottom: 16, color: '#64748b' }}>
            This will unlock the device and remove all lock restrictions.
          </p>
          <Form form={unlockForm} layout="vertical">
            <Form.Item
              name="message"
              label="Unlock Message (Optional)"
              rules={[{ max: 2000, message: 'Message must not exceed 2000 characters' }]}
            >
              <Input.TextArea
                rows={3}
                placeholder="e.g., Payment received. Your device has been unlocked. Thank you!"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      {/* Send Message Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageOutlined />
            <span>Send Message to Device</span>
          </div>
        }
        open={messageModalOpen}
        onOk={handleMessageSubmit}
        onCancel={() => {
          setMessageModalOpen(false)
          messageForm.resetFields()
        }}
        okText="Send Message"
        okButtonProps={{ loading: messageMutation.isPending }}
        width={600}
      >
        <div style={{ marginTop: 24 }}>
          <p style={{ marginBottom: 16, color: '#64748b' }}>
            This will display an alert banner on the device without locking it.
          </p>
          <Form form={messageForm} layout="vertical">
            <Form.Item
              name="message"
              label="Message"
              rules={[{ required: true, message: 'Please enter a message' }, { max: 2000, message: 'Message must not exceed 2000 characters' }]}
            >
              <Input.TextArea
                rows={4}
                placeholder="e.g., Your payment is due in 3 days. Please pay on time."
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      {/* Destroy Device Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DeleteOutlined style={{ color: '#ef4444' }} />
            <span>Destroy Device</span>
          </div>
        }
        open={destroyModalOpen}
        onOk={() => destroyMutation.mutate()}
        onCancel={() => setDestroyModalOpen(false)}
        okText="Yes, Destroy"
        okButtonProps={{
          danger: true,
          loading: destroyMutation.isPending,
        }}
        cancelText="Cancel"
        width={480}
      >
        <div style={{ marginTop: 24 }}>
          <p style={{ color: '#64748b', marginBottom: 8 }}>
            This action is <strong style={{ color: '#ef4444' }}>irreversible</strong>. It will permanently destroy the device and wipe all associated data.
          </p>
          <p style={{ color: '#64748b', margin: 0 }}>
            Are you sure you want to proceed?
          </p>
        </div>
      </Modal>
    </>
  )
})

export default DeviceActions
